import { useState, useEffect, useCallback, useRef } from 'react';
import { Search, X, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { useNavigate } from '@/lib/router-compat';

interface SearchResult {
  title: string;
  href: string;
  category: string;
  description?: string;
}

const searchableContent: SearchResult[] = [
  // Industries
  { title: 'Commercial', href: '/commercial', category: 'Industries', description: 'Enterprise AI solutions' },
  { title: 'Government', href: '/government', category: 'Industries', description: 'Federal AI services' },
  
  // Services
  { title: 'All Services', href: '/services', category: 'Services', description: 'Complete service offerings' },
  { title: 'AI Engineering', href: '/service/ai-engineering', category: 'Services', description: 'Machine learning and AI development' },
  { title: 'Enterprise IT', href: '/service/enterprise-it', category: 'Services', description: 'Infrastructure and systems' },
  
  { title: 'Program Management', href: '/service/program-management', category: 'Services', description: 'Project delivery' },
  { title: 'Strategy Consulting', href: '/service/strategy-consulting', category: 'Services', description: 'Business strategy' },
  { title: 'Supply Chain', href: '/service/supply-chain', category: 'Services', description: 'Supply chain optimization' },
  { title: 'AI Strategy', href: '/service/ai-strategy', category: 'Services', description: 'AI roadmap planning' },
  { title: 'Data Engineering', href: '/service/data-engineering', category: 'Services', description: 'Data pipelines and analytics' },
  { title: 'MLOps', href: '/service/mlops', category: 'Services', description: 'ML operations and deployment' },
  { title: 'Multi-Agent Systems', href: '/service/multi-agent-systems', category: 'Services', description: 'Agent orchestration' },
  
  // Products
  { title: 'All Products', href: '/products', category: 'Products', description: 'Product catalog' },
  { title: 'Compute Solutions', href: '/products/compute', category: 'Products', description: 'Computing infrastructure' },
  { title: 'Hardware', href: '/products/hardware', category: 'Products', description: 'Technology hardware' },
  { title: 'Software', href: '/products/software', category: 'Products', description: 'Custom agentic software engineering, evaluation, and enterprise integration' },
  
  // Company
  { title: 'About Us', href: '/about', category: 'Company', description: 'Our story and mission' },
  { title: 'Partnerships', href: '/partnership', category: 'Company', description: 'Partner with us' },
  { title: 'Contract Vehicles', href: '/contract-vehicles', category: 'Company', description: 'Government contracts' },
  { title: 'Certifications', href: '/certifications', category: 'Company', description: 'Industry certifications' },
  { title: 'News', href: '/news', category: 'Company', description: 'Latest updates' },
  
  // Join Us
  { title: 'Careers', href: '/careers', category: 'Join Us', description: 'Job opportunities' },
  { title: 'AI Innovation Network', href: '/ai-network', category: 'Join Us', description: 'Join our network' },

  // Sign in
  { title: 'Employee Portal', href: '/employee/login', category: 'Sign in', description: 'Employee access' },
  { title: 'Client Portal', href: '/client/login', category: 'Sign in', description: 'Client access' },
  { title: 'Member Portal', href: '/member/login', category: 'Sign in', description: 'Member access' },
  
  // Legal
  { title: 'Privacy Policy', href: '/privacy-policy', category: 'Legal', description: 'Data privacy' },
  { title: 'Terms of Service', href: '/terms-of-service', category: 'Legal', description: 'Usage terms' },
];

export const SiteSearch = () => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const navigate = useNavigate();

  // Keyboard shortcut
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
    };

    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, []);

  const filteredResults = query.length > 0
    ? searchableContent.filter((item) =>
        item.title.toLowerCase().includes(query.toLowerCase()) ||
        item.description?.toLowerCase().includes(query.toLowerCase()) ||
        item.category.toLowerCase().includes(query.toLowerCase())
      )
    : searchableContent;

  const groupedResults = filteredResults.reduce((acc, item) => {
    if (!acc[item.category]) {
      acc[item.category] = [];
    }
    acc[item.category].push(item);
    return acc;
  }, {} as Record<string, SearchResult[]>);

  const handleSelect = (href: string) => {
    setOpen(false);
    setQuery('');
    navigate(href);
  };

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setOpen(true)}
        className="hidden md:flex"
        aria-label="Search site"
      >
        <Search className="h-5 w-5" />
      </Button>

      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput
          placeholder="Search pages..."
          value={query}
          onValueChange={setQuery}
        />
        <CommandList>
          <CommandEmpty>No results found.</CommandEmpty>
          {Object.entries(groupedResults).map(([category, items]) => (
            <CommandGroup key={category} heading={category}>
              {items.map((item) => (
                <CommandItem
                  key={item.href}
                  value={`${item.title} ${item.description}`}
                  onSelect={() => handleSelect(item.href)}
                  className="flex items-center justify-between cursor-pointer"
                >
                  <div>
                    <span className="font-medium">{item.title}</span>
                    {item.description && (
                      <span className="ml-2 text-muted-foreground text-sm">
                        {item.description}
                      </span>
                    )}
                  </div>
                  <ArrowRight className="h-4 w-4 text-muted-foreground" />
                </CommandItem>
              ))}
            </CommandGroup>
          ))}
        </CommandList>
      </CommandDialog>
    </>
  );
};

export default SiteSearch;
